import { useLightMode } from '@/hooks/useLightMode'

import { Text } from '@chakra-ui/react'

import {
  CheckSquareIcon,
  FacilityPill,
  StatusPill,
  StatusPillVariant,
  TableCellProps,
  UserAvatar,
} from '@hivewatch/dark'

import { GuardTourStatus } from '@/graphql/generated/schemas'
import { formatDateTime } from '@/utils/formatDateTime'

import { GuardToursRowDataType } from '../types/table'

export interface GuardToursColumnIProps {
  key: string
  header: string
  style: Omit<TableCellProps, 'children'>
  sortColumn?: string
  cell: (row: GuardToursRowDataType) => React.ReactNode
}

const statusConfig: Record<
  string,
  { variant: StatusPillVariant; label: string; hasIcon: boolean }
> = {
  [GuardTourStatus.Completed]: {
    variant: 'complete',
    label: 'Complete',
    hasIcon: true,
  },
  [GuardTourStatus.InProgress]: {
    variant: 'in-progress',
    label: 'In Progress',
    hasIcon: false,
  },
  [GuardTourStatus.Incomplete]: {
    variant: 'incomplete',
    label: 'Incomplete',
    hasIcon: false,
  },
}

const getStatusConfig = (status: GuardTourStatus | null) =>
  statusConfig[status] ?? {
    variant: 'incomplete' as StatusPillVariant,
    label: 'Unknown',
    hasIcon: false,
  }

export const useColumns = (): GuardToursColumnIProps[] => {
  const mode = useLightMode()

  return [
    {
      key: 'submittedTime',
      header: 'Submitted',
      style: { minWidth: '150px', maxWidth: '350px', flexGrow: 1 },
      sortColumn: 'submittedTime',
      cell: (row) => (
        <Text color={`text.secondary.${mode}`} isTruncated>
          {formatDateTime(row.submittedTime, 'HH:mm | MM/dd/yyyy')}
        </Text>
      ),
    },
    {
      key: 'tourPlan',
      header: 'Tour Plan',
      style: { minWidth: '200px', maxWidth: '410px', flexGrow: 1 },
      cell: (row) => (
        <Text color={`text.primary.${mode}`} isTruncated>
          {row.tourPlan}
        </Text>
      ),
    },
    {
      key: 'facilityName',
      header: 'Facility',
      style: { minWidth: '200px', maxWidth: '350px', flexGrow: 1 },
      cell: (row) => (
        <FacilityPill colorScheme={mode}>{row.facilityName}</FacilityPill>
      ),
    },
    {
      key: 'guardFullName',
      header: 'Guard',
      style: { minWidth: '150px', maxWidth: '350px', flexGrow: 1 },
      cell: (row) => <UserAvatar colorScheme={mode} name={row.guardFullName} />,
    },
    {
      key: 'status',
      header: 'Status',
      style: { minWidth: '150px', maxWidth: '350px', flexGrow: 1 },
      cell: (row) => {
        const { variant, label, hasIcon } = getStatusConfig(row.status)
        return (
          <StatusPill
            colorScheme={mode}
            icon={hasIcon ? <CheckSquareIcon /> : undefined}
            label={label}
            variant={variant}
          />
        )
      },
    },
  ]
}
